import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { DndContext, PointerSensor, closestCenter, useDraggable, useDroppable, useSensor, useSensors, } from '@dnd-kit/core';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { IconButton } from '../../atoms/IconButton/IconButton.js';
import { FieldMessage } from '../../atoms/FieldMessage/FieldMessage.js';
import * as Icon from '../../icons.js';
import { cn } from '../../lib/cn.js';
import { SILENT_DRAG_ACCESSIBILITY } from '../../lib/drag.js';
import { useTranslate } from '../../lib/i18n.js';
import { LiveRegion } from '../Feedback/Feedback.js';
import { applyDrop, moveBy, placeAt, remaining, removeFrom } from './ordering.js';
import { RichText } from '../../atoms/RichText/RichText.js';
/**
 * Put the options in order (the ASSOCIATION question type).
 *
 * Two areas: the answer, which is a numbered list of positions, and the
 * options still to place. An option is moved by dragging it or by pressing a
 * button, and the two do exactly the same thing because both call the same
 * pure functions in `ordering.ts`.
 *
 * **The buttons are not a fallback.** Dragging needs a pointer and a place to
 * drag to; a keyboard user has neither, and on a phone a drag competes with
 * the scroll. Move up, move down, place and return are the whole interaction
 * on their own, and every one of them is a real button in the tab order rather
 * than a key handler bolted to a div. The grip is for a pointer and nothing
 * else: it is not a tab stop, because the buttons already do everything a
 * keyboard drag could, in one press each.
 *
 * **Focus stays with the work.** A button press re-renders the lists, and the
 * button pressed is often not there any more — or is still there but now
 * belongs to a different option. So after every press, focus is put back
 * deliberately:
 *
 * - Move up or down: the same button on the same option, which has just
 *   moved. Pressed again, it moves that option again. At either end it is
 *   disabled, and focus takes the option's other move button.
 * - Add or remove: the same button on the next option in the list the
 *   student was working in — or the one before, if that was the last — so a
 *   student placing options one by one keeps pressing the same key. Once that
 *   list is empty, focus follows the option to its first button in the other
 *   list.
 *
 * The live region says where the option went, so either way the student
 * knows what moved.
 *
 * **Nothing here knows the right order.** The student-facing GraphQL type has
 * no field for it and the repository behind it never loads the column, so this
 * is the third lock rather than the first. There is no `correct` prop and
 * nothing for one to render.
 */
export function AssociationOrdering({ legend, options, value, onChange, disabled = false, error, idPrefix, className, }) {
    const t = useTranslate();
    const generatedId = useId();
    const prefix = idPrefix ?? generatedId;
    const [dragging, setDragging] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const byId = new Map(options.map((option) => [option.id, option]));
    // An id the version no longer has is dropped rather than rendered blank: a
    // saved answer outlives nothing here, but a stale client could send one.
    const placed = value.filter((id) => byId.has(id));
    const unplaced = remaining(options, placed);
    // A slot per option: the answer is finished when every position is filled,
    // so the number of positions is the number of options and does not change.
    const slots = options.map((_, index) => placed[index]);
    const sensors = useSensors(
    // A few pixels before a drag starts, so a tap on the grip that was meant
    // for the button beside it is still a tap.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    const root = useRef(null);
    /**
     * Where focus goes once a button's move has rendered: the first of these
     * that is on screen and enabled. Set by the buttons only — a drag leaves
     * focus to the pointer that made it.
     */
    const focusNext = useRef(null);
    useLayoutEffect(() => {
        const wanted = focusNext.current;
        if (!wanted)
            return;
        focusNext.current = null;
        const buttons = [
            ...(root.current?.querySelectorAll('button[data-ordering-control]') ?? []),
        ];
        for (const [control, optionId] of wanted) {
            const button = buttons.find((candidate) => candidate.dataset['orderingControl'] === control &&
                candidate.dataset['orderingOption'] === optionId);
            if (button && !button.disabled) {
                button.focus();
                return;
            }
        }
    });
    function textOf(optionId) {
        return byId.get(optionId)?.text ?? '';
    }
    function apply(next, message) {
        onChange(next);
        setAnnouncement(message);
    }
    /** The option after this one in a list, then the one before it: whichever exists. */
    function neighbours(list, optionId) {
        const at = list.indexOf(optionId);
        return [list[at + 1], list[at - 1]].filter((id) => id !== undefined);
    }
    // The three moves. A drag and a button press both end up here, so what a
    // drop means and what a button means cannot drift apart.
    function place(optionId, index) {
        const next = placeAt(placed, optionId, index);
        apply(next, t('ui.ordering.announceMoved', [textOf(optionId), next.indexOf(optionId) + 1]));
    }
    function takeOut(optionId) {
        apply(removeFrom(placed, optionId), t('ui.ordering.announceReturned', [textOf(optionId)]));
    }
    function nudge(optionId, delta) {
        const next = moveBy(placed, optionId, delta);
        apply(next, t('ui.ordering.announceMoved', [textOf(optionId), next.indexOf(optionId) + 1]));
    }
    // The buttons: the same moves, and then where focus goes after each.
    function pressAdd(optionId) {
        const stillToPlace = unplaced.map((option) => option.id);
        focusNext.current = [
            ...neighbours(stillToPlace, optionId).map((id) => ['add', id]),
            ['up', optionId],
            ['down', optionId],
            ['remove', optionId],
        ];
        place(optionId, placed.length);
    }
    function pressRemove(optionId) {
        focusNext.current = [
            ...neighbours(placed, optionId).map((id) => ['remove', id]),
            ['add', optionId],
        ];
        takeOut(optionId);
    }
    function pressMove(optionId, delta) {
        const [same, other] = delta < 0 ? ['up', 'down'] : ['down', 'up'];
        focusNext.current = [
            [same, optionId],
            [other, optionId],
            ['remove', optionId],
        ];
        nudge(optionId, delta);
    }
    function onDragEnd(event) {
        setDragging(null);
        const optionId = String(event.active.id).slice(prefix.length + 1);
        const target = event.over?.data.current;
        // Dropped on nothing. The option stays where it was rather than being
        // guessed into a position the student did not aim at.
        if (!target)
            return;
        const next = applyDrop(placed, optionId, target);
        apply(next, target.kind === 'available'
            ? t('ui.ordering.announceReturned', [textOf(optionId)])
            : t('ui.ordering.announceMoved', [textOf(optionId), next.indexOf(optionId) + 1]));
    }
    function onDragStart(event) {
        setDragging(String(event.active.id).slice(prefix.length + 1));
    }
    const body = (
    /**
     * A container query, not a viewport one: what decides whether two columns
     * fit is how much room *this list* has, not how wide the window is. Gated
     * on the viewport, an ordering list inside a narrow panel on a wide screen
     * splits into two columns it cannot afford and the cards overflow into
     * each other.
     */
    _jsx("div", { className: "@container", children: _jsxs("div", { className: "grid gap-6 @3xl:grid-cols-2", children: [_jsxs("section", { "aria-labelledby": `${prefix}-answer-heading`, className: "flex min-w-0 flex-col gap-3", children: [_jsx("h3", { id: `${prefix}-answer-heading`, className: "text-label font-semibold uppercase tracking-wide text-text-muted", children: t('ui.ordering.answerHeading') }), _jsx("ol", { "aria-labelledby": `${prefix}-answer-heading`, className: "flex flex-col gap-2", children: slots.map((optionId, index) => (_jsx(AnswerSlot, { prefix: prefix, index: index, disabled: disabled, filled: optionId !== undefined, children: optionId === undefined ? (_jsx("p", { className: "px-2 py-1 text-body-sm text-text-muted", children: t('ui.ordering.dropHere') })) : (_jsx(OrderingChip, { prefix: prefix, optionId: optionId, text: textOf(optionId), disabled: disabled, hidden: dragging === optionId, actions: _jsxs(_Fragment, { children: [_jsx(IconButton, { icon: _jsx(Icon.ChevronUp, {}), label: t('ui.ordering.moveUp', [textOf(optionId)]), disabled: disabled || index === 0, onClick: () => pressMove(optionId, -1), "data-ordering-control": "up", "data-ordering-option": optionId }), _jsx(IconButton, { icon: _jsx(Icon.ChevronDown, {}), label: t('ui.ordering.moveDown', [textOf(optionId)]), disabled: disabled || index === placed.length - 1, onClick: () => pressMove(optionId, 1), "data-ordering-control": "down", "data-ordering-option": optionId }), _jsx(IconButton, { icon: _jsx(Icon.Undo2, {}), label: t('ui.ordering.remove', [textOf(optionId)]), disabled: disabled, onClick: () => pressRemove(optionId), "data-ordering-control": "remove", "data-ordering-option": optionId })] }) })) }, index))) })] }), _jsxs("section", { "aria-labelledby": `${prefix}-options-heading`, className: "flex min-w-0 flex-col gap-3", children: [_jsx("h3", { id: `${prefix}-options-heading`, className: "text-label font-semibold uppercase tracking-wide text-text-muted", children: t('ui.ordering.availableHeading') }), _jsx(AvailableArea, { prefix: prefix, disabled: disabled, empty: unplaced.length === 0, labelledBy: `${prefix}-options-heading`, children: unplaced.map((option) => (_jsx("li", { children: _jsx(OrderingChip, { prefix: prefix, optionId: option.id, text: option.text, disabled: disabled, hidden: dragging === option.id, actions: _jsx(IconButton, { icon: _jsx(Icon.Plus, {}), label: t('ui.ordering.add', [option.text]), disabled: disabled, onClick: () => pressAdd(option.id), "data-ordering-control": "add", "data-ordering-option": option.id }) }) }, option.id))) })] })] }) }));
    return (_jsxs("fieldset", { ref: root, className: cn('flex flex-col gap-6', className), children: [_jsx("legend", { className: "mb-6 max-w-measure-max text-question text-text text-pretty", children: _jsx(RichText, { children: legend }) }), _jsx("p", { className: "text-body-sm text-text-secondary", children: t('ui.ordering.instructions') }), disabled ? (body) : (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: onDragStart, onDragEnd: onDragEnd, onDragCancel: () => setDragging(null), accessibility: SILENT_DRAG_ACCESSIBILITY, children: body })), error ? _jsx(FieldMessage, { tone: "error", children: error }) : null, _jsx(LiveRegion, { message: announcement })] }));
}
/** One position in the answer. A drop target whether or not it is filled. */
function AnswerSlot({ prefix, index, filled, disabled, children, }) {
    const { setNodeRef, isOver } = useDroppable({
        id: `${prefix}-slot-${index}`,
        data: { kind: 'slot', index },
        disabled,
    });
    return (_jsxs("li", { className: "flex items-stretch gap-3", children: [_jsx("span", { "aria-hidden": "true", className: "flex size-8 shrink-0 items-center justify-center self-center rounded-full border-2 border-border-strong text-body-sm font-semibold text-text-secondary tabular-nums", children: index + 1 }), _jsx("div", { ref: setNodeRef, className: cn('flex min-h-touch-target-min min-w-0 flex-1 items-center rounded-option border-2 transition-colors', 
                // Filled and empty are told apart by a solid border against a dashed
                // one, not by colour alone.
                filled ? 'border-solid border-primary bg-primary-light' : 'border-dashed border-border', isOver && !filled && 'border-primary bg-primary-light', isOver && filled && 'border-primary'), children: children })] }));
}
/** Where the options wait. Dropping here takes an option back out of the answer. */
function AvailableArea({ prefix, disabled, empty, labelledBy, children, }) {
    const t = useTranslate();
    const { setNodeRef, isOver } = useDroppable({
        id: `${prefix}-available`,
        data: { kind: 'available' },
        disabled,
    });
    return (_jsx("ul", { ref: setNodeRef, "aria-labelledby": labelledBy, className: cn('flex min-h-24 flex-col gap-2 rounded-md border-2 border-dashed p-2 transition-colors', isOver ? 'border-primary bg-primary-light' : 'border-border'), children: empty ? (_jsx("li", { className: "px-2 py-1 text-body-sm text-text-muted", children: t('ui.ordering.allPlaced') })) : (children) }));
}
/**
 * One option, wherever it currently is.
 *
 * The drag listeners are on the grip alone rather than on the whole card, so
 * the buttons beside it stay buttons and a touch anywhere else on the card can
 * still scroll the page.
 *
 * Only the pointer listeners, never the library's `attributes`: those make the
 * grip a nameless `role="button"` tab stop announced as "draggable", one per
 * option, in front of the buttons that already do the moving.
 */
function OrderingChip({ prefix, optionId, text, disabled, hidden, actions, }) {
    const { listeners, setNodeRef, transform } = useDraggable({
        id: `${prefix}-${optionId}`,
        disabled,
    });
    return (_jsxs("div", { ref: setNodeRef, style: transform
            ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 }
            : undefined, className: cn('flex w-full items-center gap-2 rounded-option bg-surface px-2 py-1.5', 'border-2 border-transparent', hidden && 'opacity-50'), children: [disabled ? null : (_jsx("span", { "aria-hidden": "true", ...listeners, 
                // touch-none is what lets a drag start on a finger: without it the
                // browser claims the gesture for scrolling before dnd-kit sees it.
                className: "flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-sm text-text-muted", children: _jsx(Icon.GripVertical, { "aria-hidden": "true", className: "size-icon-md" }) })), _jsx("span", { className: "min-w-0 flex-1 break-words text-body font-medium text-text text-pretty", children: _jsx(RichText, { inline: true, children: text }) }), _jsx("span", { className: "flex shrink-0 items-center gap-1", children: actions })] }));
}
//# sourceMappingURL=AssociationOrdering.js.map